import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Scale, CheckCircle2, XCircle, AlertCircle } from 'lucide-react'
import { employeeApi, getEmployeeData } from '../../lib/employeeApi'

interface Product  { id: string; nom: string; poidsReglementaire: number; tolerance?: number; fournisseurId?: string; fournisseurNom?: string }
interface Supplier { id: string; nom: string }

type Step = 'info' | 'form' | 'result'

interface Result {
  resultat: 'CONFORME' | 'NON_CONFORME'
  poidsTotal: number
  seuilMini: number
  produit: string
  fournisseur: string
  quantite: number
}

export default function MobileBvpControl() {
  const navigate = useNavigate()
  const emp = getEmployeeData()

  const [step, setStep] = useState<Step>('info')
  const [products, setProducts] = useState<Product[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [form, setForm] = useState({ produitId: '', fournisseurId: '', quantite: '10', poidsTotal: '' })
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState<Result | null>(null)

  useEffect(() => {
    employeeApi.get('/employee/me/bvp/products').then(r => setProducts(r.data)).catch(() => {})
    employeeApi.get('/employee/me/bvp/suppliers').then(r => setSuppliers(r.data)).catch(() => {})
  }, [])

  const selectedProduct = products.find(p => p.id === form.produitId)
  const selectedSupplier = suppliers.find(s => s.id === form.fournisseurId)

  const handleSubmit = async () => {
    if (!selectedProduct || !selectedSupplier || !form.poidsTotal || !emp) return
    setSaving(true)
    try {
      const r = await employeeApi.post('/employee/me/bvp/controls', {
        employeeId: emp.id,
        employeeName: `${emp.firstName} ${emp.lastName !== '-' ? emp.lastName : ''}`.trim(),
        produit: selectedProduct.nom,
        fournisseur: selectedSupplier.nom,
        quantite: Number(form.quantite),
        poidsTotal: Number(form.poidsTotal),
        poidsReglementaire: selectedProduct.poidsReglementaire,
        tolerance: selectedProduct.tolerance ?? 5,
      })
      setResult({
        resultat: r.data.resultat,
        poidsTotal: r.data.poidsTotal,
        seuilMini: r.data.seuilMini,
        produit: selectedProduct.nom,
        fournisseur: selectedSupplier.nom,
        quantite: Number(form.quantite),
      })
      setStep('result')
    } catch { alert('Erreur lors de l\'enregistrement') }
    finally { setSaving(false) }
  }

  const reset = () => {
    setForm({ produitId: '', fournisseurId: '', quantite: '10', poidsTotal: '' })
    setResult(null)
    setStep('info')
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b px-4 flex items-center gap-3 sticky top-0 z-10"
        style={{ paddingTop: 'max(16px, env(safe-area-inset-top))', paddingBottom: '16px' }}>
        <button onClick={() => navigate('/app/bvp')} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft size={20} className="text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="font-bold text-gray-900 flex items-center gap-2">
            <Scale size={18} className="text-blue-600" /> Contrôle des poids
          </h1>
          <p className="text-xs text-gray-500">{emp?.firstName}</p>
        </div>
      </div>

      <div className="flex-1 px-4 py-6 flex flex-col gap-4">

        {/* Étape 1 — popup réglementaire */}
        {step === 'info' && (
          <div className="bg-blue-50 border-2 border-blue-300 rounded-2xl p-5">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle size={24} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-blue-900 text-base mb-2">Information réglementaire</p>
                <p className="text-sm text-blue-800 leading-relaxed">
                  La réglementation impose normalement la pesée de <strong>10 unités</strong>.
                </p>
                <p className="text-sm text-blue-800 leading-relaxed mt-2">
                  Si moins de 10 unités sont disponibles, indiquez la quantité réellement pesée.
                </p>
              </div>
            </div>
            <button onClick={() => setStep('form')}
              className="w-full bg-blue-600 text-white font-bold py-4 rounded-xl text-lg">
              Commencer le contrôle
            </button>
          </div>
        )}

        {/* Étape 2 — formulaire */}
        {step === 'form' && (
          <div className="space-y-4">
            {/* Produit */}
            <div className="bg-white rounded-2xl border p-4">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Produit</label>
              <div className="mt-3 grid grid-cols-1 gap-2">
                {products.map(p => (
                  <button key={p.id} onClick={() => setForm(f => ({ ...f, produitId: p.id, fournisseurId: p.fournisseurId || f.fournisseurId }))}
                    className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-colors ${form.produitId === p.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="font-semibold text-gray-900">{p.nom}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{p.poidsReglementaire} g • tol. ±{p.tolerance ?? 5} g/u{p.fournisseurNom ? ` • ${p.fournisseurNom}` : ''}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Fournisseur */}
            <div className="bg-white rounded-2xl border p-4">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Fournisseur</label>
                {form.produitId && products.find(p => p.id === form.produitId)?.fournisseurId === form.fournisseurId && form.fournisseurId && (
                  <span className="text-xs text-blue-500 font-medium">Auto-sélectionné</span>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {suppliers.map(s => (
                  <button key={s.id} onClick={() => setForm(f => ({ ...f, fournisseurId: s.id }))}
                    className={`text-left px-3 py-3 rounded-xl border-2 transition-colors ${form.fournisseurId === s.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="font-semibold text-sm text-gray-900">{s.nom}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Quantité */}
            <div className="bg-white rounded-2xl border p-4">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Quantité pesée</label>
              <div className="mt-3 grid grid-cols-5 gap-2">
                {[5, 6, 7, 8, 9, 10].map(n => (
                  <button key={n} onClick={() => setForm(f => ({ ...f, quantite: String(n) }))}
                    className={`py-3 rounded-xl border-2 font-bold text-lg transition-colors ${form.quantite === String(n) ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-700'}`}>
                    {n}
                  </button>
                ))}
              </div>
              <input type="number" value={form.quantite} placeholder="Autre quantité"
                onChange={e => setForm(f => ({ ...f, quantite: e.target.value }))}
                className="mt-3 w-full border-2 rounded-xl px-4 py-3 text-center text-xl font-bold" />
            </div>

            {/* Poids total */}
            <div className="bg-white rounded-2xl border p-4">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Poids total pesé (g)</label>
              {selectedProduct && form.quantite && (
                <p className="text-xs text-gray-400 mt-1">
                  Seuil mini = {selectedProduct.poidsReglementaire * Number(form.quantite) - (selectedProduct.tolerance ?? 5) * Number(form.quantite)} g
                </p>
              )}
              <input type="number" value={form.poidsTotal} placeholder="ex : 1480"
                onChange={e => setForm(f => ({ ...f, poidsTotal: e.target.value }))}
                className="mt-3 w-full border-2 rounded-xl px-4 py-4 text-center text-3xl font-bold" />
            </div>

            <button
              onClick={handleSubmit}
              disabled={!form.produitId || !form.fournisseurId || !form.poidsTotal || saving}
              className="w-full bg-blue-600 text-white font-bold py-5 rounded-2xl text-xl disabled:opacity-50">
              {saving ? 'Enregistrement...' : 'Valider le contrôle'}
            </button>
          </div>
        )}

        {/* Étape 3 — résultat */}
        {step === 'result' && result && (
          <div className="space-y-4">
            <div className={`rounded-2xl p-6 ${result.resultat === 'CONFORME' ? 'bg-green-50 border-2 border-green-400' : 'bg-red-50 border-2 border-red-400'}`}>
              <div className="flex items-center gap-3 mb-4">
                {result.resultat === 'CONFORME'
                  ? <CheckCircle2 size={40} className="text-green-500" />
                  : <XCircle size={40} className="text-red-500" />
                }
                <div>
                  <p className={`text-2xl font-black ${result.resultat === 'CONFORME' ? 'text-green-700' : 'text-red-700'}`}>
                    {result.resultat === 'CONFORME' ? 'CONFORME' : 'NON CONFORME'}
                  </p>
                  <p className="text-sm text-gray-600 mt-0.5">Contrôle enregistré</p>
                </div>
              </div>
              <div className="space-y-1 text-sm text-gray-700">
                <p><strong>Produit :</strong> {result.produit}</p>
                <p><strong>Fournisseur :</strong> {result.fournisseur}</p>
                <p><strong>Quantité :</strong> {result.quantite} unités</p>
                <p><strong>Poids pesé :</strong> {result.poidsTotal} g</p>
                <p><strong>Seuil minimum :</strong> {result.seuilMini} g</p>
              </div>
            </div>

            {result.resultat === 'NON_CONFORME' && (
              <div className="bg-red-100 border-2 border-red-300 rounded-2xl p-4">
                <p className="font-bold text-red-800 mb-2">⚠️ Action requise</p>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• Retirer les produits de la vente</li>
                  <li>• Prévenir le manager</li>
                </ul>
              </div>
            )}

            <button onClick={reset}
              className="w-full bg-blue-600 text-white font-bold py-4 rounded-2xl text-lg">
              Nouveau contrôle
            </button>
            <button onClick={() => navigate('/app/bvp')}
              className="w-full border-2 border-gray-300 text-gray-700 font-semibold py-3 rounded-2xl">
              Retour au menu BVP
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
